import type { Column } from '@/components/ui/DataTable'
import {
  favoriteColumn,
  sourceLinkColumn,
  statusColumn,
  tagsColumn,
} from '@/components/review/columns'
import { PreviouslyRejectedFlag } from '@/components/review/VerdictHistory'
import type { Quote } from '@/types'
import type { StatusFilter } from '@/components/review/StatusTabs'
import { speakerLine } from './speaker'

/**
 * Status is hidden on a single-status tab and shown on Favorites, where the
 * rows are mixed (docs/03-page-specs.md §6.1).
 */
export function buildColumns({
  status,
  onFavorite,
}: {
  status: StatusFilter
  onFavorite: (id: string, favorite: boolean) => void
}): Column<Quote>[] {
  const columns: Column<Quote>[] = [
    favoriteColumn<Quote>(onFavorite),
    {
      key: 'text',
      header: 'Quote',
      sortBy: (q) => q.text,
      render: (q) => (
        <span className="block max-w-[52ch]">
          <span className="line-clamp-2 text-[13px] leading-5 text-ink">“{q.text}”</span>
          {q.previouslyRejected && (
            <span className="mt-1 inline-block">
              <PreviouslyRejectedFlag compact />
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'speaker',
      header: 'Speaker',
      width: '190px',
      sortBy: (q) => q.speakerName ?? '',
      render: (q) => (
        <span className="block">
          <span className="block font-head text-[13px] font-semibold">{q.speakerName || '—'}</span>
          <span className="block text-[12px] text-ink-faint">{speakerLine(q)}</span>
        </span>
      ),
    },
    sourceLinkColumn<Quote>((q) => q.sourceUrl, { showPath: true, header: 'Page' }),
    tagsColumn<Quote>(2, '160px'),
  ]

  if (status === 'favorites') columns.push(statusColumn<Quote>())

  return columns
}
